import {
  LinkEntity,
  MessageEntity,
  MessageSendingStatus,
} from "@/indexedDB/types";

import {
  addMessagesToLocalDB,
  getMessageByServerIdFromLocalDB,
  getCommentCountByThreadIdFromLocalDB,
  getThreadCommentsByChatIdAndMessageIdFromLocalDB,
  updateMessageByServerIdInLocalDB,
} from "@/indexedDB/services/messages";
import { runDecryptionRetryPass } from "@/middleware/decryptionRetryOrchestrator";
import { featureFlags } from "@/config/featureFlags";
import { isUserAuthenticated } from "./auth";
// import { getMessages } from "@/server/services/messageService";
// import { MessageCategory } from "@/server/types/message";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
// import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";

import { useNavigationState } from "@/zustand/NavigationState";

import { processMessageLink } from "./links";

import { addLinksToLocalDB } from "@/indexedDB/services/links";

// import { processMessageContent, processParentMessage } from "./messages";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { umbra_getComments } from "@/server/services/umbra/messageService";
import {
  umbra_processMessageContent,
  umbra_processParentMessage,
} from "./umbra_messages";
import { extractTimestampFromUUID7, generateUuid7 } from "@/utils/uuid7";

export const umbra_processThreadMessages = async ({
  chatId,
  threadId,
  eCSK,
  after,
  before,
  pageSize,
  signal,
}: {
  chatId: string;
  threadId: string;
  eCSK: string;
  after?: string;
  before?: string;
  pageSize?: number;
  signal?: AbortSignal;
}): Promise<MessageEntity[]> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return [];
  }
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    let output: MessageEntity[] = [];

    const umbraComments = await umbra_getComments({
      chatId,
      threadId,
      after,
      before,
      pageSize,
      signal,
    });

    const eMessages = umbraComments.messages;

    if (!eMessages.length) {
      return output;
    }

    output = await umbra_processMessageContent(chatId, eCSK, eMessages);

    return output;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return [];
  }
};

/**
 * Process the side-artifacts of a fetched comment batch: hydrate the parent
 * message for any reply whose parent isn't already in the batch, and resolve +
 * store link metadata for comments that contain a link. Collected once per
 * batch then written in a single bulk op (idempotent — bulkPut / link upsert by
 * id — so it's safe to run on overlapping batches). Shared by the first-phase
 * load, the progressive loop, and reconcile-on-open so the three can't diverge.
 */
const umbra_processCommentExtras = async (
  batch: MessageEntity[],
  chatId: string,
  eCSK: string,
  signal?: AbortSignal,
): Promise<void> => {
  const parentMsgs: MessageEntity[] = [];
  const links: LinkEntity[] = [];

  for (const msg of batch) {
    // TO DO => check if parent msg already processed - to avoid extra request
    if (msg.isReply) {
      // check if parent msg is already processed in this batch
      const parentIndex = batch.findIndex(
        (b) =>
          b.id === msg.parentMessage?.id &&
          b.timestamp === msg.parentMessage?.timestamp,
      );

      // if parent msg not found in batch
      if (parentIndex === -1) {
        const parentMsg = await umbra_processParentMessage({
          messageId: msg.parentMessage?.id || "",
          chatId,
          eCSK,
        });
        if (parentMsg) {
          parentMsgs.push(parentMsg);
        }
      }
    }

    if (msg.content.link) {
      const linkData = await processMessageLink({
        messageId: msg.id,
        chatId,
        timestamp: msg.timestamp,
        link: msg.content.link,
        withMetadata: true,
        signal,
      });

      if (linkData) {
        links.push(linkData);
      }
    }
  }

  if (parentMsgs.length > 0) {
    await addMessagesToLocalDB(parentMsgs);
  }
  if (links.length > 0) {
    await addLinksToLocalDB(links);
  }
};

/**
 * Treat a comment row as server-acked. Optimistic rows authored on this device
 * carry PENDING (or FAILED if the send errored) until the server confirms; they
 * also have a temporary `id = _clientId` (a local UUID7 that can sort *newer*
 * than real server comments). Anything else (SENT / DELIVERED / READ, or a
 * legacy row with no status) is treated as acked.
 */
const isAckedComment = (m: MessageEntity): boolean =>
  m.sendingStatus !== MessageSendingStatus.PENDING &&
  m.sendingStatus !== MessageSendingStatus.FAILED;

/**
 * Reconcile a thread that was already fully loaded once, on reopen.
 *
 * Comments arrive only over the live socket, so any created while this client
 * was offline are missed — and the old loader early-returned on
 * `commentFullMsgListInit`, so reopening never recovered them. This refetches
 * the delta of new comments and is **authoritative by FETCHED ROWS, never by
 * `comment_count`** (the count is a racy read-modify-write on the BE and drifts).
 *
 * Forward delta (CREATE recovery only):
 *  - cursor = newest SERVER-ACKED local comment id. It must NOT be an optimistic
 *    PENDING/FAILED row: its id is a local UUID7 that can sort newer than real
 *    server comments, and the server `after` filter is `id > ?`, so a pending
 *    cursor would SKIP other people's comments (Codex HIGH).
 *  - page newest-first via `after` (fixed lower bound) + a descending `before`
 *    window until a short page proves the delta is drained. Upsert by
 *    `_clientId` (idempotent vs the socket and `?after` backfill paths).
 *
 * Idempotent and self-correcting: a dropped/mis-ordered update heals on the
 * next open. Loader state (`commentFullMsgListInit`, batch/render counters,
 * `commentTake`) is intentionally left untouched — the render path live-queries
 * Dexie, so upserted rows surface without touching the load state machine.
 *
 * DELETES are NOT handled here. A comment delete emits a plain `Message` Delete
 * with a valid chat_id → it lands in sync_events and `handleThreadCommentDelete`
 * removes the row + decrements the parent count on reconnect (no thread-open
 * needed). A reconcile-side prune would be dead code: the cursor is the newest
 * acked comment, so by construction no acked local row can fall in the fetched
 * `(cursor, ∞)` window — there is nothing to prune. So this stays create-only.
 *
 * Exported for unit testing (it is the meat of the reopen path).
 */
export const umbra_reconcileThreadComments = async (
  chatId: string,
  threadId: string,
  eCSK: string,
  max?: number,
  signal?: AbortSignal,
): Promise<void> => {
  const cap = max || 5000;

  // Local comments for this thread, newest-first (capped like the loader).
  const localComments = await getThreadCommentsByChatIdAndMessageIdFromLocalDB(
    chatId,
    threadId,
    cap,
  );

  // Pending-safe cursor: newest *acked* comment (list is newest-first → the
  // first acked row is the newest acked). undefined → no acked baseline yet
  // (e.g. the thread had zero comments when fully loaded) → fetch from newest.
  const afterCursor = localComments.find(isAckedComment)?.id;

  // Forward-delta paging: comments with id > afterCursor, newest-first, walking
  // the `before` upper bound down until a short page proves the delta is drained.
  let before: string | undefined = undefined;
  let fetchedAny = false;
  const maxPages = Math.ceil(cap / MESSAGE_LIST_PAGE_SIZE) + 1; // hard stop

  for (let page = 0; page < maxPages; page++) {
    if (signal?.aborted) {
      return;
    }

    const batch: MessageEntity[] = await umbra_processThreadMessages({
      chatId,
      threadId,
      eCSK,
      after: afterCursor,
      before,
      pageSize: MESSAGE_LIST_PAGE_SIZE,
      signal,
    });

    // The thread may have been closed while the fetch was in flight; bail
    // before writing (mirrors the first-phase loader's post-fetch check).
    if (signal?.aborted) {
      return;
    }

    if (batch.length === 0) {
      break;
    }

    // Forward delta returns only comments newer than our newest acked, so these
    // are genuinely new (or our own just-acked optimistic row keyed by the same
    // `_clientId`) — bulkPut upsert is safe and won't clobber good local rows.
    await addMessagesToLocalDB(batch);
    fetchedAny = true;
    await umbra_processCommentExtras(batch, chatId, eCSK, signal);

    if (batch.length < MESSAGE_LIST_PAGE_SIZE) {
      break;
    }
    // Page further back within the (afterCursor, before) window. Batch is
    // newest-first, so its last element is the oldest fetched id.
    before = batch[batch.length - 1].id;
  }

  // Thread-open does NOT trigger the decryption-retry orchestrator (its only
  // triggers are socket-connect / sync-complete / active-chat-change), so any
  // backfilled comment that failed to decrypt inline would otherwise sit on
  // "Decrypting…" forever. Kick the chat-scoped retry pass explicitly (no-op
  // when the retry feature flag is off).
  await runDecryptionRetryPass(chatId);

  // Best-effort freshness marker (does not gate correctness).
  if (fetchedAny) {
    await updateMessageByServerIdInLocalDB(threadId, {
      commentLastSyncedAt: new Date().toISOString(),
    });
  }
};

export const umbra_progressivelyLoadThreadComments = async (
  chatId: string,
  threadId: string,
  max?: number,
  signal?: AbortSignal,
) => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }
  // Check for user authentication.
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const localDbChat = await getChatFromLocalDB(chatId);
    const localThreadMessage = await getMessageByServerIdFromLocalDB(threadId);

    if (!localDbChat || !localThreadMessage) {
      return;
    }
    // TO DO => implement caching with TTL on CSKs
    const eCSK = localDbChat.eCSK;

    // Previously this returned (load-once), so a thread that gained comments
    // while the client was offline never recovered them — comments stream in
    // only via the live socket, so anything missed offline was lost on reopen.
    // Now: reconcile an already-loaded thread against the server (fetch the
    // delta of new comments; deletes recover via the events path). A
    // not-yet-fully-loaded thread still falls through to the initial/progressive
    // load below.
    //
    // Gated on the new-sync flag: this is part of the VITE_USE_NEW_SYNC feature
    // (pre-GA, internal only). Without the flag, keep the prior load-once
    // behavior so other clients don't take an extra delta fetch on every reopen.
    if (localThreadMessage.commentFullMsgListInit) {
      if (featureFlags.useNewSync) {
        await umbra_reconcileThreadComments(
          chatId,
          threadId,
          eCSK,
          max,
          signal,
        );
      }
      return;
    }

    // if first phase not completed
    if (!localThreadMessage.commentFirstPhaseMsgListInit) {
      // Fetch a batch starting at the current offset.
      const firstBatch = await umbra_processThreadMessages({
        chatId,
        threadId,
        eCSK,
        before: generateUuid7(),
        pageSize: MESSAGE_LIST_PAGE_SIZE,
        signal,
      });

      if (signal?.aborted) {
        return;
      }

      if (firstBatch.length > 0) {
        // add messages to local db
        await addMessagesToLocalDB(firstBatch);
        // Mark the first loading phase as complete.
        const commentMetadata = {
          commentFirstCommentTimestamp: firstBatch[firstBatch.length - 1].id,
          commentFirstPhaseMsgListInit: true,
          // commentLastSyncedAt: new Date().toISOString(),
          commentLastSyncedAt: extractTimestampFromUUID7(firstBatch[0].id), // set to latest msg timestamp in batch
          commentBatchCount: 1, // first batch
        };

        await updateMessageByServerIdInLocalDB(threadId, {
          ...commentMetadata,
        });

        // if chat id is active
        const activeThreadId = useNavigationState.getState().activeThreadId;
        if (threadId === activeThreadId) {
          await updateMessageByServerIdInLocalDB(threadId, {
            commentRenderedBatchCount: 1, // first rendered batch
          });
        }

        // hydrate parent messages for replies + resolve link metadata
        await umbra_processCommentExtras(firstBatch, chatId, eCSK, signal);

        // if first batch is less than page size, mark the whole list as complete and exit
        if (firstBatch.length < MESSAGE_LIST_PAGE_SIZE) {
          await updateMessageByServerIdInLocalDB(threadId, {
            commentFullMsgListInit: true,
            // commentLastSyncedAt: new Date().toISOString(),
          });

          return;
        }
      } else {
        // if no msg in first batch mark everything as complete
        await updateMessageByServerIdInLocalDB(threadId, {
          commentFirstPhaseMsgListInit: true,
          commentFullMsgListInit: true,
          // commentLastSyncedAt: new Date().toISOString(),
        });

        return;
      }
    }

    let moreComments: MessageEntity[] = [];
    let currentCommentCount: number;

    do {
      // extra check on abort controller
      if (signal?.aborted) {
        break;
      }

      const _localThreadMessage =
        await getMessageByServerIdFromLocalDB(threadId);

      if (!_localThreadMessage) {
        break;
      }

      moreComments = await umbra_processThreadMessages({
        chatId,
        threadId,
        eCSK,
        pageSize: MESSAGE_LIST_PAGE_SIZE,
        before: _localThreadMessage.commentFirstCommentTimestamp,
        signal,
      });

      // Exit loop if no messages are returned
      if (!moreComments.length) {
        break;
      }

      await updateMessageByServerIdInLocalDB(threadId, {
        commentFirstCommentTimestamp: moreComments[moreComments.length - 1].id,
        // commentLastSyncedAt: new Date().toISOString(),
        commentLastSyncedAt: extractTimestampFromUUID7(moreComments[0].id), // set to latest msg timestamp in batch
        commentBatchCount: (_localThreadMessage.commentBatchCount || 0) + 1, // increment batch count
      });

      // add messages to local db
      await addMessagesToLocalDB(moreComments);

      // hydrate parent messages for replies + resolve link metadata
      await umbra_processCommentExtras(moreComments, chatId, eCSK, signal);

      // if result is less than MESSAGE_LIST_PAGE_SIZE during loop - exit
      if (moreComments.length < MESSAGE_LIST_PAGE_SIZE) {
        //  Finalize the state by marking progressive init as finished.
        await updateMessageByServerIdInLocalDB(threadId, {
          commentFullMsgListInit: true,
        });

        break;
      }

      currentCommentCount =
        await getCommentCountByThreadIdFromLocalDB(threadId);
    } while (moreComments.length > 0 && currentCommentCount <= (max || 5000));

    // TODO: Discuss with Andrei:
    //  - why updating lastSyncedAt here?

    // Because  -> //  Finalize the loop by setting lastSyncedAt at the time of finishing the loop.
    // Also  -> //  Finalize the loop by marking progressive init as finished in local state.
    // TO DO => Temporary
    await updateMessageByServerIdInLocalDB(threadId, {
      commentFullMsgListInit: true,
    });
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot load message list.");
  }
};
